import {useState, useRef, useEffect, useCallback, createContext, useContext, useReducer} from '@wordpress/element';
import {useSelect} from '@wordpress/data';
import {STORE_KEY} from '@wc-braintree/block-data';
import {actionReducer, useActionEmitter, emittFilter} from "../context";
import {loadClient} from "../utils";
import {useNoticeHandler} from "../hooks";

const PaymentMethodDataContext = createContext();

export const PaymentMethodDataProvider = (
    {
        children,
        container,
        onSubmit,
        getData,
        isExpress = null,
        setExpressPaymentError
    }) => {
    const [client, setClient] = useState();
    const [paymentMethodData, __setPaymentMethodData] = useState({});
    const [eventHandlers, dispatch] = useReducer(actionReducer, {});
    const {onPaymentDataFilter} = useActionEmitter(dispatch);
    const currentEventHandlers = useRef(eventHandlers);
    const currentData = useRef({onSubmit});
    const [notice, addNotice, removeNotice] = useNoticeHandler({isExpress, setExpressPaymentError});

    const {
        fastlane,
        paymentToken,
        customerFound
    } = useSelect(select => {
        const store = select(STORE_KEY);
        return {
            fastlane: store.getFastlane(),
            paymentToken: store.getPaymentToken(),
            customerFound: store.isCustomerFound()
        };
    });

    const setPaymentMethodData = useCallback((values) => {
        __setPaymentMethodData(prev => ({
            ...prev,
            ...values
        }))
    }, []);

    const setPaymentMethodNonce = useCallback((nonce => {
        setPaymentMethodData({nonce});
    }), [setPaymentMethodData]);

    const doPaymentDataFilter = useCallback(async (data, args) => {
        return await emittFilter(data, args, {
            handlers: currentEventHandlers.current,
            type: 'payment_data_filter'
        });
    }, []);

    useEffect(() => {
        currentEventHandlers.current = eventHandlers;
    }, [eventHandlers]);

    useEffect(() => {
        loadClient.then(client => setClient(client)).catch(error => {
            addNotice(error)
        });
    }, [addNotice]);

    useEffect(() => {
        currentData.current = {onSubmit};
    }, [onSubmit]);

    useEffect(() => {
        if (paymentMethodData?.nonce) {
            currentData.current.onSubmit();
        }
    }, [paymentMethodData]);

    const paymentMethodDataContext = {
        container,
        client,
        paymentMethodData,
        setPaymentMethodData,
        setPaymentMethodNonce,
        onPaymentDataFilter,
        doPaymentDataFilter,
        onSubmit,
        notice: {
            notice,
            addNotice,
            removeNotice
        },
        fraud: {
            enabled: getData('advancedFraudEnabled')
        },
        threeDSecureEnabled: getData('threeDSecureEnabled'),
        fastlane: {
            fastlane,
            paymentToken,
            customerFound
        }

    };
    return (
        <PaymentMethodDataContext.Provider value={paymentMethodDataContext}>
            {children}
        </PaymentMethodDataContext.Provider>
    )
}

export const usePaymentMethodDataContext = () => {
    return useContext(PaymentMethodDataContext);
}